import {InViewHookResponse, useInView} from 'react-intersection-observer';

import pickDefined from 'util/pickDefined';

type HasBeenInViewHookResponse = Pick<InViewHookResponse, 'ref' | 'entry'> & {hasBeenInView?: boolean};

/**
 * Hook for determining whether or not an element has ever been within the user's viewport.
 *
 * If the enabled boolean parameter is true (default), hasBeenInView will be a boolean.
 * If it is false, hasBeenInView will be undefined.
 *
 * IMPORTANT: If IntersectionObserver is not supported by the user's browser, we force-disable this hook,
 * meaning hasBeenInView will be undefined (and therefore falsy).
 *
 * TODO(BNCH-30090): Make hook output truthy (or more explicit) when disabled.
 * See comments from Somak and Eli B on https://github.com/benchling/aurelia/pull/26908 for ideas.
 *
 * hasBeenInView will always be false for the first render as the ref returned by the hook needs to be
 * assigned to the element before the IntersectionObserver API can compute whether or not the element
 * is in view. After that, it will become and stay true once the element is or comes into the user's viewport.
 *
 * This is useful for delaying the rendering of expensive components until they are within view of the user.
 *
 * Example usage:
 *   function Component() {
 *     const {ref, hasBeenInView} = useHasBeenInView();
 *     return (
 *       <div ref={ref}>
 *         {hasBeenInView ? <ExpensiveToRenderComponent /> : <PlaceholderComponent />}
 *       </div>
 *     );
 *   }
 */
export default function useHasBeenInView(enabled: boolean = true): HasBeenInViewHookResponse {
  // triggerOnce = true: inView will only change from false to true once, and will not
  //   change from true to false. More technically, the row is unobserved by the IntersectionObserver API
  //   after it has come into view.
  // skip: If true, the element will not be observed by the IntersectionObserver API. This can be used to
  //   improve performance since there is overhead to start observing an element with IntersectionObserver.

  // Only enable if the browser has support for the IntersectionObserver
  enabled = enabled && typeof IntersectionObserver !== 'undefined';
  const useInViewResponse = useInView({triggerOnce: true, skip: !enabled});
  return {
    ...pickDefined(useInViewResponse, 'ref', 'entry'),
    hasBeenInView: enabled ? useInViewResponse.inView : undefined,
  };
}
